import {
  addDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  runTransaction,
  startAfter,
  Timestamp,
  where,
  type CollectionReference,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type UpdateData,
  type WithFieldValue,
} from 'firebase/firestore'
import { refs, type SchoolAccountDoc } from '../models/base/firestore-refs'
import type { SchoolAccount } from '../models/account/school/school-account.modl'
import { toSchoolAccount } from '../models/account/school/school-account.modl'
import type {
  SchoolAccountHistoryDoc,
  SchoolAccountHistoryEntity,
} from '../models/account/school/school-account-history.modl'
import { toSchoolAccountHistoryEntity } from '../models/account/school/school-account-history.modl'
import type { AdminAccount } from '../models/account/admin/admin-account.modl'
import {
  createDoc,
  getAll,
  getOne,
  softDelete,
  transactionSetWithBaseFields,
  transactionUpdateWithUpdatedAt,
} from './firestore-crud'
import { getFirebaseApp } from '../config/firebase.config'
import { MailService } from './mail-service'
import { normalizePostalCodeForStorage } from '../utils/postal-code'
import { formatLocaleDateTimeJa } from '../utils/date-util'

export type PaginatedResult<T> = {
  data: T[]
  lastDoc: QueryDocumentSnapshot | null
  hasNextPage: boolean
}

export type SchoolAccountHistoryViewItem = {
  id: string
  date: string
  user: string
  action: string
  content: string
}

/**
 * SchoolAccount（school_accounts）用サービス
 *
 * school と schoolAccount を統合し、school_accounts のみを扱う。
 */
export class SchoolAccountService {
  /**
   * 学校アカウント一覧を取得する（admin 用）
   */
  async getAllSchoolAccounts(includeDeleted: boolean = false): Promise<SchoolAccount[]> {
    try {
      return await getAll(refs.schoolAccounts(), toSchoolAccount, { includeDeleted })
    } catch (error) {
      console.error('Error getting school accounts:', error)
      throw error
    }
  }

  /**
   * 学校アカウントを1件取得する
   */
  async getSchoolAccount(schoolAccountId: string, includeDeleted: boolean = false): Promise<SchoolAccount | null> {
    try {
      return getOne(
        refs.schoolAccount(schoolAccountId),
        (id, data, path) => toSchoolAccount(id, data as SchoolAccountDoc, path),
        { includeDeleted },
      )
    } catch (error) {
      console.error('Error getting school account:', error)
      throw error
    }
  }

  /**
   * 学校アカウントを利用停止にする（履歴作成＋論理削除＋再取得）
   * @returns 更新後の SchoolAccount、失敗時は null
   */
  async deactivateSchoolAccountWithHistory(
    account: SchoolAccount,
    adminAccount: AdminAccount,
  ): Promise<SchoolAccount | null> {
    const historyRef = refs.schoolAccountHistories(account.id)
    const schoolAccountJson: SchoolAccountDoc & { document_id?: string } = {
      school_type: account.schoolType,
      school_name: account.schoolName,
      postal_code: account.postalCode,
      prefecture: account.prefecture,
      city: account.city,
      address: account.address,
      phone: account.phone,
      principal: account.principal,
      description: account.description ?? null,
      contact_name: account.contactName,
      contact_email: account.contactEmail,
      name_kanji: account.nameKanji,
      name_kana: account.nameKana,
      email: account.email,
      last_login_at: account.lastLoginAt ? Timestamp.fromDate(account.lastLoginAt) : null,
      created_at: Timestamp.fromDate(account.created_at),
      updated_at: Timestamp.fromDate(account.updated_at),
      is_deleted: account.is_deleted,
      is_test_school: account.isTestSchool === true,
      document_id: account.id,
    }
    await createDoc(
      historyRef as CollectionReference<SchoolAccountHistoryDoc, SchoolAccountHistoryDoc>,
      {
        school_account: schoolAccountJson,
        updated_by_user_type: 'admin',
        updated_by_user_id: adminAccount.id,
        updated_by_name: adminAccount.nameKanji ?? '',
        updated_by_user_document_path: adminAccount.path ?? '',
        update_info: '利用停止',
      } as WithFieldValue<SchoolAccountHistoryDoc>,
      { isDeleted: false },
    )
    await softDelete(refs.schoolAccount(account.id))
    return this.getSchoolAccount(account.id, true)
  }

  /**
   * school_accounts をページング取得する
   *
   * - 並び順: updated_at desc（更新日が新しい順）
   * - includeDeleted=false のとき is_deleted==false のみ
   */
  async getPaginated(
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot | null,
    includeDeleted: boolean = false,
  ): Promise<PaginatedResult<SchoolAccount> | null> {
    const baseConstraints: QueryConstraint[] = []
    if (!includeDeleted) baseConstraints.push(where('is_deleted', '==', false))

    // UI側が「更新日」を出しているので updated_at でページングする
    baseConstraints.push(orderBy('updated_at', 'desc'))
    baseConstraints.push(limit(pageSize + 1))

    if (lastDocument) {
      baseConstraints.push(startAfter(lastDocument))
    }

    const q = query(refs.schoolAccounts(), ...baseConstraints)
    const snapshot = await getDocs(q)
    const docs = snapshot.docs

    const hasNextPage = docs.length > pageSize
    const dataDocs = hasNextPage ? docs.slice(0, pageSize) : docs
    const lastDoc = dataDocs.length > 0 ? (dataDocs[dataDocs.length - 1] as QueryDocumentSnapshot) : null

    const data = dataDocs.map((d) => toSchoolAccount(d.id, d.data() as SchoolAccountDoc, d.ref.path))

    return { data, lastDoc, hasNextPage }
  }

  /**
   * 学校アカウントの履歴をページング取得する
   *
   * - 並び順: created_at desc（新しい順）
   * - includeDeleted=false のとき is_deleted==false のみ
   */
  async getHistoryPaginated(
    schoolAccountId: string,
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot | null,
    includeDeleted: boolean = false,
  ): Promise<PaginatedResult<SchoolAccountHistoryEntity> | null> {
    const baseConstraints: QueryConstraint[] = []
    if (!includeDeleted) baseConstraints.push(where('is_deleted', '==', false))
    baseConstraints.push(orderBy('created_at', 'desc'))
    baseConstraints.push(limit(pageSize + 1))
    if (lastDocument) baseConstraints.push(startAfter(lastDocument))

    const q = query(refs.schoolAccountHistories(schoolAccountId), ...baseConstraints)
    const snapshot = await getDocs(q)
    const docs = snapshot.docs

    const hasNextPage = docs.length > pageSize
    const dataDocs = hasNextPage ? docs.slice(0, pageSize) : docs
    const lastDoc = dataDocs.length > 0 ? (dataDocs[dataDocs.length - 1] as QueryDocumentSnapshot) : null

    const data = dataDocs.map((d) =>
      toSchoolAccountHistoryEntity(d.id, d.data() as SchoolAccountHistoryDoc, d.ref.path),
    )

    return { data, lastDoc, hasNextPage }
  }

  /**
   * 学校アカウント履歴を画面表示向けに整形して返す
   */
  async getHistoryViewItems(
    schoolAccountId: string,
    pageSize: number = 100,
  ): Promise<SchoolAccountHistoryViewItem[]> {
    if (!schoolAccountId) return []

    const result = await this.getHistoryPaginated(schoolAccountId, pageSize, null, false)
    const histories = result?.data ?? []

    return histories.map((history) => {
      const updateInfo = history.update_info ?? null
      const action = updateInfo === '利用停止' ? '利用停止' : '更新'
      const content = updateInfo
        ? `${updateInfo}${updateInfo === '利用停止' ? 'にしました' : 'を更新しました'}`
        : '学校情報を更新しました'

      return {
        id: history.id,
        date: formatLocaleDateTimeJa(history.created_at),
        user: history.updated_by_name,
        action,
        content,
      }
    })
  }

  /**
   * 学校アカウントの単一フィールドを更新する（admin 編集）
   * - DB から最新を取得して履歴に保存し、更新する
   */
  async updateFieldAsAdmin(params: {
    schoolAccountId: string
    firestoreField: string
    value: string | null | boolean
    updateInfo: string
    adminAccount: { id: string; nameKanji?: string; path?: string }
  }): Promise<SchoolAccount | null> {
    return this.updateFieldWithHistory({
      ...params,
      updatedBy: {
        userType: 'admin',
        userId: params.adminAccount.id,
        userName: params.adminAccount.nameKanji ?? '管理者',
        userPath: params.adminAccount.path ?? '',
      },
    })
  }

  /**
   * 学校アカウントの単一フィールドを更新する（学校自身の編集）
   * - DB から最新を取得して履歴に保存し、更新する
   */
  async updateFieldAsSchool(params: {
    schoolAccountId: string
    firestoreField: string
    value: string | null | boolean
    updateInfo: string
    schoolAccount: { id: string; schoolName: string; path: string }
  }): Promise<SchoolAccount | null> {
    return this.updateFieldWithHistory({
      ...params,
      updatedBy: {
        userType: 'school',
        userId: params.schoolAccount.id,
        userName: params.schoolAccount.schoolName,
        userPath: params.schoolAccount.path,
      },
    })
  }

  /**
   * 学校ユーザー操作の履歴を1件追加する（フィールド更新を伴わない操作用）
   */
  async addHistoryAsSchool(params: {
    schoolAccountId: string
    updateInfo: string
    schoolAccount: { id: string; schoolName: string; path: string }
  }): Promise<void> {
    const account = await this.getSchoolAccount(params.schoolAccountId, true)
    if (!account) {
      throw new Error('学校アカウントが見つかりません')
    }

    const historyRef = refs.schoolAccountHistories(params.schoolAccountId)
    const schoolAccountJson: SchoolAccountDoc & { document_id?: string } = {
      school_type: account.schoolType,
      school_name: account.schoolName,
      postal_code: account.postalCode,
      prefecture: account.prefecture,
      city: account.city,
      address: account.address,
      phone: account.phone,
      principal: account.principal,
      description: account.description ?? null,
      contact_name: account.contactName,
      contact_email: account.contactEmail,
      name_kanji: account.nameKanji,
      name_kana: account.nameKana,
      email: account.email,
      last_login_at: account.lastLoginAt ? Timestamp.fromDate(account.lastLoginAt) : null,
      created_at: Timestamp.fromDate(account.created_at),
      updated_at: Timestamp.fromDate(account.updated_at),
      is_deleted: account.is_deleted,
      is_test_school: account.isTestSchool === true,
      document_id: account.id,
    }

    await createDoc(
      historyRef as CollectionReference<SchoolAccountHistoryDoc, SchoolAccountHistoryDoc>,
      {
        school_account: schoolAccountJson,
        updated_by_user_type: 'school',
        updated_by_user_id: params.schoolAccount.id,
        updated_by_name: params.schoolAccount.schoolName,
        updated_by_user_document_path: params.schoolAccount.path,
        update_info: params.updateInfo,
      } as WithFieldValue<SchoolAccountHistoryDoc>,
      { isDeleted: false },
    )
  }

  /**
   * 単一フィールドを更新し、履歴に保存する（内部共通処理）
   */
  private async updateFieldWithHistory(params: {
    schoolAccountId: string
    firestoreField: string
    value: string | null | boolean
    updateInfo: string
    updatedBy: {
      userType: 'admin' | 'school'
      userId: string
      userName: string
      userPath: string
    }
  }): Promise<SchoolAccount | null> {
    const { schoolAccountId, firestoreField, value, updateInfo, updatedBy } = params
    const writeValue =
      typeof value === 'boolean'
        ? value
        : firestoreField === 'postal_code' && typeof value === 'string'
          ? normalizePostalCodeForStorage(value)
          : value
    const accountRef = refs.schoolAccount(schoolAccountId)
    const historyCollectionRef = refs.schoolAccountHistories(schoolAccountId)
    const db = getFirestore(getFirebaseApp())

    await runTransaction(db, async (transaction) => {
      const currentSnap = await transaction.get(accountRef)
      if (!currentSnap.exists()) return
      const currentDoc = currentSnap.data() as SchoolAccountDoc
      const schoolAccountSnapshot: SchoolAccountDoc & { document_id?: string } = {
        ...currentDoc,
        document_id: schoolAccountId,
      }
      transactionSetWithBaseFields(
        transaction,
        historyCollectionRef as CollectionReference<SchoolAccountHistoryDoc, SchoolAccountHistoryDoc>,
        {
          school_account: schoolAccountSnapshot,
          updated_by_user_type: updatedBy.userType,
          updated_by_user_id: updatedBy.userId,
          updated_by_name: updatedBy.userName,
          updated_by_user_document_path: updatedBy.userPath,
          update_info: updateInfo,
        } as WithFieldValue<SchoolAccountHistoryDoc>,
        { isDeleted: false },
      )
      transactionUpdateWithUpdatedAt(transaction, accountRef, {
        [firestoreField]: writeValue,
      } as UpdateData<SchoolAccountDoc>)
    })

    return this.getSchoolAccount(schoolAccountId)
  }

  /**
   * メールアドレスで学校アカウントが存在するかチェック（is_deleted に関わらず）
   * @param email - チェックするメールアドレス
   * @returns 存在する場合 true、存在しない場合 false
   */
  async checkEmailExists(email: string): Promise<boolean> {
    const q = query(
      refs.schoolAccounts(),
      where('contact_email', '==', email.trim()),
    )
    const snapshot = await getDocs(q)
    return snapshot.docs.length > 0
  }

  /**
   * contact_email で学校アカウントを1件取得する（is_deleted は含めない）
   * @param email - 連絡先メールアドレス
   * @returns 取得した SchoolAccount、存在しない場合は null
   */
  async getSchoolAccountByContactEmail(email: string): Promise<SchoolAccount | null> {
    const q = query(
      refs.schoolAccounts(),
      where('contact_email', '==', email.trim()),
    )
    const snapshot = await getDocs(q)
    const doc = snapshot.docs.find((d) => !d.data()?.is_deleted)
    if (!doc) return null
    return toSchoolAccount(doc.id, doc.data() as SchoolAccountDoc, doc.ref.path)
  }

  /**
   * パスワード再設定用メールを送信する（mail コレクションにドキュメントを追加）。
   * SendMailFromFirestore トリガーにより実際のメールが送信される。
   * @param params.to - 送信先メールアドレス
   * @param params.resetLink - パスワード再設定用リンク（Firebase Auth の generatePasswordResetLink 等で取得）
   */
  async sendPasswordResetEmail(params: { to: string; resetLink: string }): Promise<void> {
    const mailRef = refs.mail()
    await addDoc(
      mailRef,
      MailService.passwordReset({
        to: params.to,
        resetLink: params.resetLink,
      }),
    )
  }
}

